import React, { useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import { LoadingError } from "../../../components/LoadingFork";
import LoadingIndicator from "../../../components/LoadingIndicator";
import { withInstallation } from "../../../components/InstallationContext";
import { T, formatClock } from "../constants";
import { cx, useUi } from "../ui";
import Panel from "../components/Panel";
import RefreshIcon from "../dashboard/components/RefreshIcon";
import useShiftPerformance from "./useShiftPerformance";
import { formatTimeOfDay } from "../shifts/shiftHelpers";
import {
  EMPTY,
  formatAssignment,
  formatDuration,
  porterName,
  totalJobs,
} from "./helpers";

const CARD_STYLE = { boxShadow: "none", border: `1px solid ${T.border}`, borderRadius: 12, overflowX: "auto" };

const useStyles = makeStyles({
  root: { maxWidth: 1200, margin: "0 auto" },
  titleBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  title: { fontSize: 24, fontWeight: 700, color: T.fg, margin: 0 },
  controls: { display: "flex", alignItems: "center", gap: 8 },
  shiftSelect: { width: "auto", maxWidth: 220, height: 32, padding: "0 8px", fontSize: 12 },
  refreshBtn: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    borderRadius: 6,
    border: `1px solid ${T.border}`,
    background: T.elevated,
    color: T.mutedFg,
    cursor: "pointer",
    fontFamily: "inherit",
    "&:hover": { background: T.surface, color: T.fg },
    "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
  },
  "@keyframes portersRefreshSpin": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
  refreshBtnSpinning: { "& svg": { animation: "$portersRefreshSpin 0.9s linear infinite" } },

  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: T.subtleFg,
    padding: "12px 20px",
    borderBottom: `1px solid ${T.borderSoft}`,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  thNumber: { textAlign: "right" },
  td: {
    padding: "14px 20px",
    borderBottom: `1px solid ${T.borderSoft}`,
    verticalAlign: "middle",
  },
  tdNumber: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
  name: { fontWeight: 600, color: T.fg },
  status: { fontSize: 12, color: T.mutedFg, marginTop: 2 },
  assignment: { color: T.fg },
  none: { color: T.subtleFg },
  total: { fontWeight: 600 },
  updated: { fontSize: 12, color: T.mutedFg, padding: "12px 20px 14px" },
  empty: { padding: "24px 20px", fontSize: 13, color: T.mutedFg },
  loadingRow: { padding: 24, display: "flex", justifyContent: "center" },
});

function Porters({ installation }) {
  const s = useStyles();
  const ui = useUi();
  const installationId = installation?.id;
  const { shifts, selection, runners, loading, updatedAt, error, select, refetch } =
    useShiftPerformance({ installationId });

  const [refreshing, setRefreshing] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  }, []);

  const refresh = useCallback(async () => {
    if (!refetch) return;
    setRefreshing(true);
    try {
      await refetch();
    } catch (e) {
      /* The query's own error state already reports this; catching here only
         stops it becoming an unhandled rejection. */
    } finally {
      if (mounted.current) setRefreshing(false);
    }
  }, [refetch]);

  const options = shifts.map((shift) => ({
    value: shift.id,
    label: `${shift.name} ${formatTimeOfDay(shift.startTime)}-${formatTimeOfDay(shift.endTime)}`,
  }));

  const rows = () =>
    runners.map((runner) => {
      const assignment = runner.onShift ? formatAssignment(runner) : null;

      return (
        <tr key={runner.userId} aria-label={`Porter ${porterName(runner)}`}>
          <td className={s.td}>
            <div className={s.name}>{porterName(runner)}</div>
            <div className={s.status}>{runner.onShift ? "available" : "off shift"}</div>
          </td>
          <td className={cx(s.td, s.assignment)}>
            {assignment || <span className={s.none}>None</span>}
          </td>
          <td className={cx(s.td, s.tdNumber)}>{runner.onShift ? runner.parked : EMPTY}</td>
          <td className={cx(s.td, s.tdNumber)}>{runner.onShift ? runner.retrieved : EMPTY}</td>
          <td className={cx(s.td, s.tdNumber, s.total)}>
            {runner.onShift ? totalJobs(runner) : EMPTY}
          </td>
          <td className={cx(s.td, s.tdNumber)}>{formatDuration(runner.avgDropOffMs)}</td>
          <td className={cx(s.td, s.tdNumber)}>{formatDuration(runner.avgPickupMs)}</td>
          <td className={cx(s.td, s.tdNumber)}>{formatDuration(runner.avgHandoverMs)}</td>
        </tr>
      );
    });

  const body = () => {
    if (error) return <LoadingError error={error} />;

    if (loading) {
      return (
        <div className={s.loadingRow}>
          <LoadingIndicator />
        </div>
      );
    }

    if (runners.length === 0) {
      return <div className={s.empty}>No porters have the runner role at this hotel</div>;
    }

    return (
      <>
        <table className={s.table} aria-label="Porters">
          <thead>
            <tr>
              <th className={s.th}>Porter</th>
              <th className={s.th}>Current assignment</th>
              <th className={cx(s.th, s.thNumber)}>Parked</th>
              <th className={cx(s.th, s.thNumber)}>Pickups</th>
              <th className={cx(s.th, s.thNumber)}>Total</th>
              <th className={cx(s.th, s.thNumber)}>Drop-off</th>
              <th className={cx(s.th, s.thNumber)}>Pickup</th>
              <th className={cx(s.th, s.thNumber)}>Handover</th>
            </tr>
          </thead>
          <tbody>{rows()}</tbody>
        </table>
        <div className={s.updated}>
          {updatedAt ? `Last updated ${formatClock(updatedAt)}` : "Updating\u2026"}
        </div>
      </>
    );
  };

  return (
    <div className={s.root}>
      <div className={s.titleBar}>
        <h1 className={s.title}>Porters</h1>
        <div className={s.controls}>
          {options.length > 0 && (
            <select
              className={cx(ui.field, s.shiftSelect)}
              aria-label="Shift"
              value={selection ?? ""}
              disabled={!installationId}
              onChange={(event) =>
                select(event.target.value === "" ? undefined : event.target.value)
              }
            >
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={refresh}
            disabled={refreshing || loading}
            className={cx(s.refreshBtn, refreshing && s.refreshBtnSpinning)}
            aria-label="Refresh porters"
            title={refreshing ? "Refreshing…" : "Refresh porters"}
          >
            <RefreshIcon />
          </button>
        </div>
      </div>
      <Panel style={CARD_STYLE}>{body()}</Panel>
    </div>
  );
}

export default withInstallation(Porters);
